import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Page, Tabs, Badge, Banner, Text, BlockStack, InlineStack } from "@shopify/polaris";
import { getRequest, postRequest, formatToCurrency } from "../../shared/components/functions";
import { Toasts } from "../../shared/components/toasts/toasts";
import { PageSpinner } from "../../shared/components/spinner/PageSpinner";
import { BuyList } from "./components/buyList";
import { ToOrderList } from "./components/toOrderList";
import { PoFollowUp } from "./components/poFollowUp";
import { ExceptionsList } from "./components/exceptionsList";
import { ClearToBuild } from "./components/clearToBuild";
import { ForecastEditor } from "./components/forecastEditor";
import { SuppliersPerformance } from "./components/suppliersPerformance";
import { HowItWorks } from "./components/howItWorks";

export default function MrpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabIndex = Number(searchParams.get("tab")) || 0;

  const [selected, setSelected] = useState(tabIndex);
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [running, setRunning] = useState(false);
  const [toast, setToast] = useState(null);

  const [reportStatus, setReportStatus] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);

  const fetchLatest = useCallback(async () => {
    const latest = await getRequest("/api/mrp/latest");
    setData(latest);
    setLoaded(true);
  }, []);

  const fetchReportStatus = useCallback(async () => {
    const status = await getRequest("/api/mrp/report/status");
    setReportStatus(status);
  }, []);

  useEffect(() => {
    fetchLatest();
    fetchReportStatus();
  }, [fetchLatest, fetchReportStatus]);

  const runNow = async () => {
    setRunning(true);
    await postRequest("/api/mrp/run", {}, setToast);
    await fetchLatest();
    setRunning(false);
  };

  const sendReportNow = async () => {
    setSendingReport(true);
    await postRequest("/api/mrp/report/send", {}, setToast);
    await fetchReportStatus();
    setSendingReport(false);
  };

  const handleTabChange = (selectedTabIndex) => {
    setSelected(selectedTabIndex);
    navigate(`/mrp?tab=${selectedTabIndex}`);
  };

  if (!loaded) return <PageSpinner />;

  const run = data?.run;
  const summary = data?.summary;
  const suggestions = data?.suggestions || [];
  const buyListByPart = data?.buyListByPart || [];
  const exceptions = data?.exceptions || [];
  const clearToBuild = data?.clearToBuild || [];
  const poFollowUp = data?.poFollowUp || [];
  const highExceptions = exceptions.filter((exception) => exception.Severity === "HIGH").length;
  const criticalPos = poFollowUp.filter((po) => po.severity === "CRITICAL").length;
  const lastReportAttempt = reportStatus?.attempts?.[0];
  const lastReportSent = reportStatus?.attempts?.find((attempt) => attempt.Status === "SENT");

  const tabs = [
    {
      id: "to-order",
      content: `To order (${buyListByPart.length})`,
      component: <ToOrderList parts={buyListByPart} />,
    },
    {
      id: "buy-list",
      content: `By supplier (${suggestions.length})`,
      component: <BuyList suggestions={suggestions} setToast={setToast} />,
    },
    {
      id: "po-follow-up",
      content: `Chase POs (${poFollowUp.length})`,
      component: <PoFollowUp poFollowUp={poFollowUp} />,
    },
    {
      id: "exceptions",
      content: `Exceptions (${exceptions.length})`,
      component: <ExceptionsList exceptions={exceptions} />,
    },
    {
      id: "clear-to-build",
      content: `Clear to build (${clearToBuild.length})`,
      component: <ClearToBuild clearToBuild={clearToBuild} generatedAt={summary?.generatedAt} />,
    },
    {
      id: "forecast",
      content: "Forecast",
      component: <ForecastEditor setToast={setToast} onChanged={() => {}} />,
    },
    {
      id: "suppliers",
      content: "Suppliers",
      component: <SuppliersPerformance setToast={setToast} />,
    },
    {
      id: "how-it-works",
      content: "How it works",
      component: <HowItWorks config={data?.config} goToTab={handleTabChange} />,
    },
  ];

  const selectedTabIndex = tabs[selected] ? selected : 0;

  return (
    <Page
      fullWidth
      title="Purchase planner (MRP)"
      subtitle={run ? `Last run: ${String(run.finishedAt).replace("T", " ").substring(0, 16)} (${run.triggeredBy === "CRON" ? "nightly" : run.triggeredBy === "REPORT" ? "weekly report" : "manual"})` : "No completed run yet"}
      primaryAction={{ content: "Run now", onAction: runNow, loading: running }}
      secondaryActions={[
        { content: "Preview weekly report", onAction: () => window.open("/api/mrp/report/preview", "_blank") },
        { content: "Send weekly report now", onAction: sendReportNow, loading: sendingReport },
      ]}>
      <Toasts toast={toast} setToast={setToast} toastContent="Done" />

      <BlockStack gap="400">
        {!run && (
          <Banner title="No MRP run has completed yet" tone="info">
            <p>Press “Run now” to calculate the first buy list. The nightly run happens at 04:00.</p>
          </Banner>
        )}

        {reportStatus && !reportStatus.smtpConfigured && (
          <Banner title="Weekly report e-mail cannot be sent — SMTP is not configured" tone="critical">
            <p>
              {`The Monday buy-list mail to ${reportStatus.to} needs SMTP credentials. Set SMTP_USER and SMTP_PASS (and optionally SMTP_HOST/SMTP_PORT) in the server environment.`}
            </p>
          </Banner>
        )}

        {reportStatus && reportStatus.smtpConfigured && reportStatus.logAvailable && reportStatus.sentThisWeek === false && lastReportAttempt?.Status === "ERROR" && (
          <Banner title="This week's report e-mail has not been sent yet — the last attempt failed" tone="critical">
            <p>{`${String(lastReportAttempt.CreatedAt).replace("T", " ").substring(0, 16)}: ${lastReportAttempt.Error || "unknown error"}. It is retried automatically every hour.`}</p>
          </Banner>
        )}

        {reportStatus && !reportStatus.logAvailable && (
          <Banner title="Weekly report send log is missing" tone="warning">
            <p>Run server/sql/2026-08-mrp-report-log.sql on the database so report sends are tracked and missed Monday sends are retried automatically.</p>
          </Banner>
        )}

        {run && summary && (
          <InlineStack gap="200" blockAlign="center">
            <Badge tone="info">{`Horizon ${summary.horizonDays} days`}</Badge>
            <Badge>{`${summary.demandItems} demand lines`}</Badge>
            <Badge tone={summary.lateSuggestions > 0 ? "critical" : "success"}>{`${summary.lateSuggestions} late orders`}</Badge>
            <Badge tone={criticalPos > 0 ? "critical" : "success"}>{`${criticalPos} critical POs`}</Badge>
            <Badge tone={highExceptions > 0 ? "critical" : "success"}>{`${highExceptions} high exceptions`}</Badge>
            <Text as="span" tone="subdued">{`Open buy value: ${formatToCurrency(summary.totalBuyValue || 0)}`}</Text>
            {reportStatus?.logAvailable && (
              <Badge tone={reportStatus.sentThisWeek ? "success" : "attention"}>
                {reportStatus.sentThisWeek && lastReportSent
                  ? `Weekly mail sent ${String(lastReportSent.CreatedAt).replace("T", " ").substring(0, 16)}`
                  : "Weekly mail not sent yet this week"}
              </Badge>
            )}
          </InlineStack>
        )}

        <Tabs tabs={tabs} selected={selectedTabIndex} onSelect={handleTabChange}>
          <div style={{ paddingTop: "12px" }}>{tabs[selectedTabIndex].component}</div>
        </Tabs>
      </BlockStack>
    </Page>
  );
}
